import { trackEvent } from '@zola-helpers/client/dist/es/tracking/trackingHelper';
import _includes from 'lodash/includes';
import _endsWith from 'lodash/endsWith';

import TextUtil from './TextUtil';

const NAV_SECTIONS = {
  primary: 'PRIMARY',
  secondary: 'SECONDARY',
  tertiary: 'TERTIARY',
  footer: 'FOOTER',
  primarySubNav: 'PRIMARY_SUB_MENU',
  secondarySubNav: 'SECONDARY_SUB_MENU',
};

NAV_SECTIONS.all = [
  NAV_SECTIONS.primary,
  NAV_SECTIONS.secondary,
  NAV_SECTIONS.tertiary,
  NAV_SECTIONS.footer,
  NAV_SECTIONS.primarySubNav,
  NAV_SECTIONS.secondarySubNav,
];

// Returns (sub-menu || secondary || secondary sub-menu) top level parent's text
// With the looping + regex this is slowww but the quickest implementation
// given there is no schema for the Nav
const getNodeLevelOne = (href, isWeddingShopCategory) => {
  if (href === '/') return 'Zola';

  if (isWeddingShopCategory) return 'Wedding Boutique';

  const isChildOfWeddingShop = [
    /^\/shop\/category\/wedding-shop/,
    /^\/shop\/category\/second-looks/,
    /^\/shop\/collection\/wedding-shop-new-arrivals/,
    /^\/shop\/gift-collections\/wedding-shop-collections/,
    /^\/shop\/brands\/wedding-shop/,
    /^\/shop\/category\/bridal-boutique/,
    /^\/shop\/category\/wedding-party-attire/,
    /^\/shop\/category\/wedding-gifts-favors/,
    /^\/shop\/category\/party-supplies-decor/,
    /^\/shop\/category\/wedding-shop-accessories/,
    /^\/shop\/category\/wedding-jewelry/,
    /^\/shop\/category\/wedding-intimates/,
    /^\/shop\/category\/health-wellness/,
  ].some((regex) => regex.test(href));

  if (isChildOfWeddingShop) return 'Wedding Boutique';

  const isChildOfAccount = [
    /^\/account/,
    /^\/wedding-vendors\/inquiries/,
    /^\/wedding-registry\/invite/,
  ].some((regex) => regex.test(href));

  if (isChildOfAccount) return 'Account';

  const isChildOfRegistry = [
    /^\/registry/,
    /^\/shop/,
    /^\/wedding-registry/,
    /^\/search/,
    /^\/my-registry/,
    /^\/onboard\/new/,
  ].some((regex) => regex.test(href));

  if (isChildOfRegistry) return 'Registry';

  const isChildOfWebsite = [
    /^\/wedding-planning\/website/,
    /^\/wedding\/(manage\/website|!(manage))/,
  ].some((regex) => regex.test(href));

  if (isChildOfWebsite) return 'Website';

  const isChildOfInvites = [
    /^\/wedding-planning\/paper/,
    /^\/wedding-planning\/projects/,
    /^\/wedding-planning\/(save-the-date|invitations)/,
    /^\/wedding-planning\/(enclosures|menus|programs|place-cards|thank-you-cards|holiday-cards|change-the-date)/,
    /^\/wedding-planning\/(draper-james|lovely-bride)/, // limited time collab link
  ].some((regex) => regex.test(href));

  if (isChildOfInvites) return 'Invites + Paper';

  const isChildOfMoreTools = [
    /^\/wedding\/manage/,
    /^\/wedding-planning/,
    /^\/inspiration/,
    /^\/wedding-vendors/,
    /^\/expert-advice/,
    /https:\/\/zola.hotelplanner/,
  ].some((regex) => regex.test(href));

  if (isChildOfMoreTools) return 'More Tools';

  return '';
};

const getEventData = (node, section, titleOverride, isWeddingShopCategory) => {
  const href = node.getAttribute('href');
  const linkText = titleOverride || node.text;
  // Disable camelcase to avoid having to map back to snakecase for the BE
  /* eslint-disable camelcase */
  let menu_item = TextUtil.toAllUpperCase(linkText);
  // sometimes a new tag is used, and we need to truncate it off the end
  const isLastThreeCharsNew = _endsWith(menu_item, 'NEW');
  if (isLastThreeCharsNew) {
    // naive approach
    menu_item = menu_item.substring(0, menu_item.length - 3);
  }
  // const menu_item = TextUtil.removeSubStr(capitalizedTitle, 'NEW');
  const node_level_1 = TextUtil.toAllUpperCase(getNodeLevelOne(href, isWeddingShopCategory));
  /* eslint-enable camelcase */

  return {
    menu_item,
    section,
    node_level_1,
  };
};

const trackNavigationClickEvent = ({ node, section, titleOverride, isWeddingShopCategory }) => {
  if (!(node instanceof Node)) throw Error('Failed to supply a node');
  if (!section) throw Error('Failed to specify a section.');
  if (!_includes(NAV_SECTIONS.all, section))
    throw Error('Provided section does not match accepted format');

  // if el clicked is not a <a/> don't fire event
  const isAnchorNode = node.nodeName === 'A' && node.href;

  if (!isAnchorNode) return undefined;

  const eventData = getEventData(node, section, titleOverride, isWeddingShopCategory);
  return trackEvent('Navigation Clicked', eventData);
};

export { NAV_SECTIONS };
export default trackNavigationClickEvent;
